/**
 * @file charts.tsx
 * @brief Charts for Super Admin → Analytics (W22), in the panel's chart style.
 *
 * @details
 * Built to the dataviz skill's rules:
 * - one y-axis per chart, hairline solid grid, 2px lines, thin bars with a
 *   2px surface gap between stacked segments;
 * - a legend for every chart with two or more series, a hover tooltip on
 *   every mark, and a table beside each chart (the tabs) for the exact values;
 * - colour follows the entity (`--viz-*` tokens in index.css, validated in
 *   light and dark), never its rank; text stays in text colours.
 * - Time series never go below 0 (B25 already clamps revenue / cost / count
 *   series). A loss is drawn only as a bar to the left of a zero line
 *   (`DivergingBars`), never as a line under the axis.
 *
 * @author DES (prepared with Claude)
 * @date 25-10-2026
 */

import { type ReactNode } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/Tooltip";
import { cn } from "@/shared/theme/cn";

export interface ChartSeries {
  key: string;
  label: string;
  color: string;
}

const AXIS_TICK = { fill: "hsl(var(--muted-foreground))", fontSize: 11 } as const;

// ----------------------------------------------------------------------
// Legend
// ----------------------------------------------------------------------

export function ChartLegend({ series, className }: { series: ChartSeries[]; className?: string }) {
  return (
    <ul className={cn("flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-muted-foreground", className)} aria-label="Legend">
      {series.map((s) => (
        <li key={s.key} className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 shrink-0 rounded-[3px]" style={{ background: s.color }} aria-hidden />
          <span>{s.label}</span>
        </li>
      ))}
    </ul>
  );
}

// ----------------------------------------------------------------------
// Tooltip for the Recharts charts
// ----------------------------------------------------------------------

interface TipRow {
  dataKey?: string | number;
  value?: unknown;
  payload?: Record<string, unknown>;
}

function SeriesTooltip({
  active,
  payload,
  series,
  format,
  footer,
}: {
  active?: boolean;
  payload?: unknown;
  series: ChartSeries[];
  format: (v: number) => string;
  footer?: (row: Record<string, unknown>) => ReactNode;
}) {
  const rows = Array.isArray(payload) ? (payload as TipRow[]) : [];
  if (!active || rows.length === 0) return null;
  const point = rows[0]?.payload ?? {};
  return (
    <div className="min-w-[11rem] rounded-2xl border border-border/80 bg-popover/95 px-3 py-2.5 shadow-lg backdrop-blur-sm">
      <p className="text-[11px] font-medium text-muted-foreground">{String(point.label ?? "")}</p>
      <ul className="mt-1.5 space-y-1">
        {series.map((s) => {
          const v = Number(point[s.key]);
          return (
            <li key={s.key} className="flex items-center gap-2 text-xs">
              <span className="h-3 w-0.5 shrink-0 rounded-full" style={{ background: s.color }} aria-hidden />
              <span className="text-muted-foreground">{s.label}</span>
              <span className="ml-auto pl-3 font-medium tabular-nums">{Number.isFinite(v) ? format(v) : "–"}</span>
            </li>
          );
        })}
      </ul>
      {footer ? <div className="mt-1.5 border-t border-border/60 pt-1.5 text-xs">{footer(point)}</div> : null}
    </div>
  );
}

function EmptyPlot({ height, text }: { height: number; text: string }) {
  return (
    <div style={{ height }} className="flex items-center justify-center px-4 text-center text-sm text-muted-foreground">
      {text}
    </div>
  );
}

function LoadingPlot({ height }: { height: number }) {
  return <div style={{ height }} className="animate-pulse rounded-xl bg-muted/40" aria-label="Loading" />;
}

// ----------------------------------------------------------------------
// Lines over time (revenue vs cost)
// ----------------------------------------------------------------------

export function LinesOverTime({
  data,
  series,
  format,
  tick,
  footer,
  height = 260,
  isLoading,
  emptyText,
  isEmpty,
}: {
  /** Each row: { label, [series.key]: number ≥ 0 }. */
  data: Record<string, unknown>[];
  series: ChartSeries[];
  format: (v: number) => string;
  tick: (v: number) => string;
  footer?: (row: Record<string, unknown>) => ReactNode;
  height?: number;
  isLoading?: boolean;
  emptyText: string;
  isEmpty?: boolean;
}) {
  if (isLoading) return <LoadingPlot height={height} />;
  if (isEmpty || data.length === 0) return <EmptyPlot height={height} text={emptyText} />;
  const dots = data.length <= 14;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 12, right: 12, left: 0, bottom: 0 }}>
        <CartesianGrid vertical={false} stroke="var(--viz-grid)" />
        <XAxis dataKey="label" tick={AXIS_TICK} tickLine={false} axisLine={{ stroke: "var(--viz-grid)" }} minTickGap={24} />
        <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} width={64} tickFormatter={tick} domain={[0, "auto"]} allowDecimals />
        <RechartsTooltip
          cursor={{ stroke: "hsl(var(--muted-foreground))", strokeWidth: 1 }}
          content={(p) => <SeriesTooltip active={p.active} payload={p.payload} series={series} format={format} footer={footer} />}
        />
        {series.map((s) => (
          <Line
            key={s.key}
            type="monotone"
            dataKey={s.key}
            name={s.label}
            stroke={s.color}
            strokeWidth={2}
            dot={dots ? { r: 3, strokeWidth: 0, fill: s.color } : false}
            activeDot={{ r: 4.5, strokeWidth: 2, stroke: "hsl(var(--background))" }}
            isAnimationActive={false}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

// ----------------------------------------------------------------------
// Stacked bars over time (cost by vendor) / single bars (sign-ups)
// ----------------------------------------------------------------------

export function BarsOverTime({
  data,
  series,
  format,
  tick,
  footer,
  height = 260,
  isLoading,
  emptyText,
  isEmpty,
  counts = false,
}: {
  data: Record<string, unknown>[];
  series: ChartSeries[];
  format: (v: number) => string;
  tick: (v: number) => string;
  footer?: (row: Record<string, unknown>) => ReactNode;
  height?: number;
  isLoading?: boolean;
  emptyText: string;
  isEmpty?: boolean;
  /** Whole numbers (sign-ups): no 0.25 ticks. */
  counts?: boolean;
}) {
  if (isLoading) return <LoadingPlot height={height} />;
  if (isEmpty || data.length === 0) return <EmptyPlot height={height} text={emptyText} />;
  const stacked = series.length > 1;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 12, right: 12, left: 0, bottom: 0 }} barCategoryGap="22%">
        <CartesianGrid vertical={false} stroke="var(--viz-grid)" />
        <XAxis dataKey="label" tick={AXIS_TICK} tickLine={false} axisLine={{ stroke: "var(--viz-grid)" }} minTickGap={24} />
        <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} width={64} tickFormatter={tick} domain={[0, "auto"]} allowDecimals={!counts} />
        <RechartsTooltip
          cursor={{ fill: "hsl(var(--muted) / 0.5)" }}
          content={(p) => <SeriesTooltip active={p.active} payload={p.payload} series={series} format={format} footer={footer} />}
        />
        {series.map((s, i) => (
          <Bar
            key={s.key}
            dataKey={s.key}
            name={s.label}
            stackId={stacked ? "a" : undefined}
            fill={s.color}
            maxBarSize={28}
            // 2px surface gap between stacked segments, rounded data end on top.
            stroke={stacked ? "hsl(var(--background))" : undefined}
            strokeWidth={stacked ? 1 : 0}
            radius={!stacked || i === series.length - 1 ? [4, 4, 0, 0] : 0}
            isAnimationActive={false}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

// ----------------------------------------------------------------------
// Horizontal bars per row (HTML). Two measures per row share one scale
// (revenue vs cost); `DivergingBars` puts a loss left of a zero line.
// ----------------------------------------------------------------------

export interface PairRow {
  id: string;
  label: ReactNode;
  values: Record<string, number>;
  tip: ReactNode;
}

export function PairedBars({ rows, series, emptyText }: { rows: PairRow[]; series: ChartSeries[]; emptyText: string }) {
  const max = Math.max(0, ...rows.flatMap((r) => series.map((s) => r.values[s.key] ?? 0)));
  if (rows.length === 0 || max <= 0) return <EmptyPlot height={120} text={emptyText} />;
  return (
    <TooltipProvider delayDuration={100}>
      <ul className="space-y-2.5">
        {rows.map((r) => (
          <li key={r.id}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="grid cursor-default grid-cols-[minmax(7rem,11rem)_1fr] items-center gap-3 rounded-lg px-1 py-0.5 hover:bg-muted/40 sm:grid-cols-[12rem_1fr]">
                  <span className="truncate text-xs text-foreground/80">{r.label}</span>
                  <div className="space-y-[2px]">
                    {series.map((s) => {
                      const v = Math.max(0, r.values[s.key] ?? 0);
                      return (
                        <div key={s.key} className="h-2 w-full">
                          <div
                            className="h-full rounded-r-[4px]"
                            style={{ width: `${(v / max) * 100}%`, minWidth: v > 0 ? 2 : 0, background: s.color }}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[300px] whitespace-normal">
                {r.tip}
              </TooltipContent>
            </Tooltip>
          </li>
        ))}
      </ul>
    </TooltipProvider>
  );
}

export interface DivergingRow {
  id: string;
  label: ReactNode;
  value: number;
  shown: string;
  tip: ReactNode;
}

/** Positive right of zero (--viz-1), a loss left of zero (--viz-neg). */
export function DivergingBars({ rows, emptyText }: { rows: DivergingRow[]; emptyText: string }) {
  const maxPos = Math.max(0, ...rows.map((r) => r.value));
  const maxNeg = Math.max(0, ...rows.map((r) => -r.value));
  const span = maxPos + maxNeg;
  if (rows.length === 0 || span <= 0) return <EmptyPlot height={120} text={emptyText} />;
  const zero = (maxNeg / span) * 100;
  return (
    <TooltipProvider delayDuration={100}>
      <ul className="space-y-1.5">
        {rows.map((r) => {
          const w = (Math.abs(r.value) / span) * 100;
          const neg = r.value < 0;
          return (
            <li key={r.id}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="grid cursor-default grid-cols-[minmax(7rem,11rem)_1fr_5.5rem] items-center gap-3 rounded-lg px-1 py-0.5 hover:bg-muted/40 sm:grid-cols-[12rem_1fr_7rem]">
                    <span className="truncate text-xs text-foreground/80">{r.label}</span>
                    <div className="relative h-3">
                      <span className="absolute inset-y-[-3px] w-px bg-border" style={{ left: `${zero}%` }} aria-hidden />
                      {r.value !== 0 ? (
                        <span
                          className={cn("absolute inset-y-0", neg ? "rounded-l-[4px]" : "rounded-r-[4px]")}
                          style={{
                            left: neg ? `${zero - w}%` : `${zero}%`,
                            width: `${w}%`,
                            minWidth: 2,
                            background: neg ? "var(--viz-neg)" : "var(--viz-1)",
                          }}
                        />
                      ) : null}
                    </div>
                    <span className={cn("text-right text-xs font-medium tabular-nums", neg && "text-destructive")}>
                      {r.shown}
                    </span>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[300px] whitespace-normal">
                  {r.tip}
                </TooltipContent>
              </Tooltip>
            </li>
          );
        })}
      </ul>
    </TooltipProvider>
  );
}
